import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn } from "../components/UI";
import { FLabel, FInput, FSelect, FTextarea } from "./engagement/FormControls";
import { useAuth } from "../context/AuthContext";
import UsersPage from "./UsersPage";
import ProgramsConfig from "./ProgramsConfig";
import Billing from "./Billing";
import RolesPermissions from "./RolesPermissions";
import { Building2, Users, ShieldCheck, Boxes, CreditCard, Layers, Lock, ExternalLink, Palette, PenLine, Trash2, Plus, CheckCircle2, AlertTriangle, KeyRound, ChevronDown, ChevronUp, DoorOpen, MessageSquare, FileSignature, Archive, Wand2, ShieldAlert, Stethoscope, Cog, Radio, Sparkles, BookOpen, Pill, Store, PhoneCall, GraduationCap } from "lucide-react";
import EducationLibrarySection from "./settings/EducationLibrarySection";
import FacilitiesRooms from "./FacilitiesRooms";
import FeedbackSurveys from "./FeedbackSurveys";
import { SignaturePad } from "../components/SignaturePad";
import { TaskStatusManager } from "./TaskStatusManager";
import { toast } from "sonner";
import AuditReportSection from "./settings/AuditReportSection";
import DiagnosisLibrarySection from "./settings/DiagnosisLibrarySection";
import MedicationLibrarySection from "./settings/MedicationLibrarySection";
import PharmacyDirectorySection from "./settings/PharmacyDirectorySection";
import { NewPill } from "../components/NewPill";
import CommunicationLogSection from "./settings/CommunicationLogSection";
import DataRetentionSection from "./settings/DataRetentionSection";
import { SignatureAuditSection, DdiAuditSection, ConfigurationLibrarySection } from "./settings/V2Previews";
import BaaTrackerSection, { SessionTimeoutSection } from "./settings/BaaTrackerSection";
import { BaaTrackerV2Preview } from "./settings/V2Previews";

const ROLE_LABELS = { admin: "Administrator", clinician: "Clinician", front_desk: "Front Desk", billing: "Billing" };

function OrganizationSection() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/organization").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!form) return <div className="py-10 text-center text-sm text-[#84675B]">Loading…</div>;
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/settings/organization", form); setForm(r.data); toast.success("Organization profile saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const fields = [
    ["name", "Organization name"], ["phone", "Phone"], ["fax", "Fax"], ["email", "Email"],
    ["website", "Website"], ["address", "Street address"], ["city", "City"], ["state", "State"],
    ["zip", "ZIP"], ["npi", "NPI number"], ["tax_id", "Tax ID (EIN)"], ["medicaid_provider_id", "Medicaid provider ID"],
    ["carf_accreditation", "CARF accreditation #"], ["timezone", "Timezone"],
  ];
  return (
    <Card className="p-6" data-testid="settings-organization">
      <h3 className="mb-1 font-heading text-base font-bold text-[#3E2723]">Organization Profile</h3>
      <p className="mb-5 text-xs text-[#7B6157]">Used on letters, fax covers, printed charts & claims.</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {fields.map(([k, label]) => (
          <div key={k}><FLabel>{label}</FLabel><FInput data-testid={`org-${k}-input`} value={form[k] || ""} onChange={f(k)} /></div>
        ))}
      </div>
      <div className="mt-5 flex justify-end"><Btn onClick={save} disabled={busy} data-testid="org-save-btn">{busy ? "Saving…" : "Save Profile"}</Btn></div>
    </Card>
  );
}

function RbacSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/rbac").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!data) return <div className="py-10 text-center text-sm text-[#84675B]">Loading…</div>;
  const toggle = (domainKey, role) => {
    setData((s) => ({
      ...s,
      domains: s.domains.map((d) => d.key !== domainKey ? d : {
        ...d, roles: d.roles.includes(role) ? d.roles.filter((r) => r !== role) : [...d.roles, role],
      }),
    }));
  };
  const save = async () => {
    setBusy(true);
    try {
      const permissions = Object.fromEntries(data.domains.map((d) => [d.key, d.roles]));
      const r = await api.put("/settings/rbac", { permissions });
      setData((s) => ({ ...s, domains: r.data.domains }));
      toast.success("Permissions updated - changes apply immediately");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-6" data-testid="settings-rbac">
      <h3 className="mb-1 font-heading text-base font-bold text-[#3E2723]">Role-Based Access Control</h3>
      <p className="mb-5 text-xs text-[#7B6157]">Control which roles can edit each data domain. Administrators always retain full access. Changes are enforced server-side and audited.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-[#EFEBE0] text-left text-[11px] uppercase tracking-wide text-[#7B6157]">
              <th className="py-2 pr-4">Data Domain</th>
              {data.all_roles.map((r) => <th key={r} className="px-3 py-2 text-center">{ROLE_LABELS[r]}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.domains.map((d) => (
              <tr key={d.key} className="border-b border-[#F1EEE7]" data-testid={`rbac-row-${d.key}`}>
                <td className="py-3 pr-4">
                  <div className="font-semibold text-[#3E2723]">{d.label}</div>
                  <div className="text-xs text-[#84675B]">{d.desc}</div>
                </td>
                {data.all_roles.map((role) => {
                  const on = d.roles.includes(role);
                  const locked = role === "admin";
                  return (
                    <td key={role} className="px-3 py-3 text-center">
                      <button data-testid={`rbac-${d.key}-${role}`} disabled={locked} aria-pressed={on}
                        onClick={() => toggle(d.key, role)}
                        className={`h-6 w-11 rounded-full transition-colors relative ${on ? "bg-[#4E342E]" : "bg-[#D7CCC8]"} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}>
                        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"} left-0`} />
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-5 flex justify-end"><Btn onClick={save} disabled={busy} data-testid="rbac-save-btn">{busy ? "Saving…" : "Save Permissions"}</Btn></div>
    </Card>
  );
}

function PlansSection() {
  const [plans, setPlans] = useState(null);
  const load = () => api.get("/platform/plans").then((r) => setPlans(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!plans) return <div className="py-10 text-center text-sm text-[#84675B]">Loading plans…</div>;
  const set = (id, k, v) => setPlans((s) => s.map((p) => (p.id === id ? { ...p, [k]: v } : p)));
  const save = async (p) => {
    try {
      await api.put(`/platform/plans/${p.id}`, { name: p.name, price_monthly: Number(p.price_monthly), max_users: Number(p.max_users), max_clients: Number(p.max_clients) });
      toast.success(`${p.name} plan updated`); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="settings-plans">
      {plans.map((p) => (
        <Card key={p.id} className="p-5" data-testid={`plan-card-${p.key || p.id}`}>
          <div className="mb-3 font-heading text-base font-bold text-[#3E2723]">{p.name}</div>
          <div className="space-y-3">
            <div><FLabel>Monthly price ($)</FLabel><FInput data-testid={`plan-price-${p.id}`} type="number" value={p.price_monthly} onChange={(e) => set(p.id, "price_monthly", e.target.value)} /></div>
            <div><FLabel>Max users (seats)</FLabel><FInput data-testid={`plan-users-${p.id}`} type="number" value={p.max_users} onChange={(e) => set(p.id, "max_users", e.target.value)} /></div>
            <div><FLabel>Max clients</FLabel><FInput data-testid={`plan-clients-${p.id}`} type="number" value={p.max_clients} onChange={(e) => set(p.id, "max_clients", e.target.value)} /></div>
            <ul className="list-disc pl-4 text-xs text-[#7B6157]">{(p.features || []).map((ft, i) => <li key={i}>{ft}</li>)}</ul>
            <Btn className="w-full" onClick={() => save(p)} data-testid={`plan-save-${p.id}`}>Save Plan</Btn>
          </div>
        </Card>
      ))}
    </div>
  );
}

function BrandingSection() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/organization").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!form) return <div className="py-10 text-center text-sm text-[#84675B]">Loading…</div>;
  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400_000) return toast.error("Logo must be under 400 KB");
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, logo: reader.result }));
    reader.readAsDataURL(file);
  };
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/settings/organization", form); setForm(r.data); toast.success("Branding saved - applied to letters, forms & print"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-6" data-testid="settings-branding">
      <h3 className="mb-1 font-heading text-base font-bold text-[#3E2723]">Branding & Form Customization</h3>
      <p className="mb-5 text-xs text-[#7B6157]">Your logo and styling appear on printed letters, clinical forms, fax covers and the print center.</p>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <FLabel>Clinic logo</FLabel>
          <div className="mt-1 flex items-center gap-4">
            <div className="flex h-20 w-36 items-center justify-center rounded-xl border border-dashed border-[#D7CCC8] bg-[#FDFCF8] overflow-hidden">
              {form.logo ? <img src={form.logo} alt="Clinic logo" className="max-h-full max-w-full object-contain" data-testid="branding-logo-preview" /> : <span className="text-[11px] text-[#84675B]">No logo</span>}
            </div>
            <div className="space-y-2">
              <input id="branding-logo-file" data-testid="branding-logo-input" type="file" accept="image/*" onChange={onLogo} className="hidden" />
              <label htmlFor="branding-logo-file" className="inline-block cursor-pointer rounded-full bg-[#4E342E] px-4 py-2 text-xs font-semibold text-white hover:bg-[#5D4037]">Upload logo</label>
              {form.logo && <button data-testid="branding-logo-remove" onClick={() => setForm((s) => ({ ...s, logo: "" }))} className="block text-xs font-semibold text-[#C62828] hover:underline">Remove</button>}
            </div>
          </div>
        </div>
        <div><FLabel>Accent color</FLabel>
          <div className="flex items-center gap-2">
            <input data-testid="branding-accent-color" type="color" value={form.accent_color || "#4E342E"} onChange={(e) => setForm((s) => ({ ...s, accent_color: e.target.value }))} className="h-10 w-14 rounded-lg border border-[#E5DFD3] bg-white p-1 cursor-pointer" />
            <FInput value={form.accent_color || ""} onChange={(e) => setForm((s) => ({ ...s, accent_color: e.target.value }))} placeholder="#4E342E" className="w-28" />
          </div>
        </div>
        <div><FLabel>Letterhead style</FLabel>
          <FSelect data-testid="branding-letterhead" value={form.letterhead_style || "classic"} onChange={(e) => setForm((s) => ({ ...s, letterhead_style: e.target.value }))}>
            <option value="classic">Classic - logo left, details right</option>
            <option value="centered">Centered - logo & details centered</option>
            <option value="minimal">Minimal - name only</option>
          </FSelect>
        </div>
        <div><FLabel>Date format on forms</FLabel>
          <FSelect data-testid="branding-date-format" value={form.date_format || "MM/DD/YYYY"} onChange={(e) => setForm((s) => ({ ...s, date_format: e.target.value }))}>
            <option>MM/DD/YYYY</option><option>DD/MM/YYYY</option><option>YYYY-MM-DD</option><option>MMM D, YYYY</option>
          </FSelect>
        </div>
        <div className="sm:col-span-2"><FLabel>Footer text (printed on every document)</FLabel>
          <FTextarea data-testid="branding-footer-text" rows={2} value={form.footer_text || ""} onChange={(e) => setForm((s) => ({ ...s, footer_text: e.target.value }))} placeholder="CONFIDENTIAL - Protected Health Information. Unauthorized disclosure prohibited." />
        </div>
      </div>
      <div className="mt-5 flex justify-end"><Btn onClick={save} disabled={busy} data-testid="branding-save-btn">{busy ? "Saving…" : "Save Branding"}</Btn></div>
    </Card>
  );
}

function SignatureSection() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ signature_name: "", signature_credentials: "", signature_style: "cursive", signature_image: "" });
  const [padKey, setPadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [historyOpen, setHistoryOpen] = useState(false);

  // Sync the local form to whatever the AuthContext eventually resolves.
  // Without this, the initial useState snapshot wins even when /auth/me
  // finishes later, which is why the "save" appeared to do nothing on refresh.
  useEffect(() => {
    if (!user) return;
    setForm({
      signature_name: user.signature_name || `${user.first_name || ""} ${user.last_name || ""}`.trim(),
      signature_credentials: user.signature_credentials || "",
      signature_style: user.signature_style || "cursive",
      signature_image: user.signature_image || "",
    });
    setPadKey((k) => k + 1);  // force SignaturePad remount so it picks up the value
  }, [user?.signature_name, user?.signature_credentials, user?.signature_style, user?.signature_image]);

  const loadHistory = () =>
    api.get("/me/signature/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const save = async () => {
    // Guard against silent wipe: if the user clears the pad and hits Save,
    // require them to confirm before we blank out an existing signature.
    if (!form.signature_image && user?.signature_image) {
      if (!window.confirm("The signature canvas is empty. Saving now will remove your saved drawn signature. Continue?")) return;
    }
    setBusy(true);
    try {
      const r = await api.put("/me/signature", form);
      // Refresh AuthContext so downstream signing flows see the new signature.
      if (refresh) await refresh();
      // Update local form from server response so we display exactly what was saved.
      setForm({
        signature_name: r.data.signature_name || "",
        signature_credentials: r.data.signature_credentials || "",
        signature_style: r.data.signature_style || "cursive",
        signature_image: r.data.signature_image || "",
      });
      toast.success("Signature saved. Used when signing clinical documents.");
      loadHistory();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6" data-testid="settings-signature">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="font-heading text-base font-bold text-[#3E2723]">My E-Signature</h3>
        <button data-testid="sig-history-toggle" onClick={() => setHistoryOpen((v) => !v)}
          className="text-xs font-semibold text-[#4E342E] hover:underline">
          {historyOpen ? "Hide" : "Show"} change audit ({history.total || 0})
        </button>
      </div>
      <p className="mb-5 text-xs text-[#7B6157]">Applied when you sign and lock progress notes, treatment plans, and clinical forms.</p>
      <div className="grid gap-4 sm:grid-cols-3">
        <div><FLabel>Signature name</FLabel><FInput data-testid="sig-name-input" value={form.signature_name} onChange={(e) => setForm((s) => ({ ...s, signature_name: e.target.value }))} /></div>
        <div><FLabel>Credentials (e.g., LCSW, LPC)</FLabel><FInput data-testid="sig-credentials-input" value={form.signature_credentials} onChange={(e) => setForm((s) => ({ ...s, signature_credentials: e.target.value }))} /></div>
        <div><FLabel>Style</FLabel>
          <FSelect data-testid="sig-style-select" value={form.signature_style} onChange={(e) => setForm((s) => ({ ...s, signature_style: e.target.value }))}>
            <option value="cursive">Cursive script</option><option value="print">Print</option>
          </FSelect>
        </div>
      </div>
      <div className="mt-4">
        <FLabel>Drawn signature (applied wherever you sign)</FLabel>
        <div className="mt-1 max-w-xl">
          <SignaturePad key={padKey} value={form.signature_image} onChange={(img) => setForm((s) => ({ ...s, signature_image: img }))} />
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-4">
        <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B]">Preview</div>
        {form.signature_image && <img src={form.signature_image} alt="Drawn signature" className="mt-1 h-14 object-contain" data-testid="sig-image-preview" />}
        <div data-testid="sig-preview" className={`mt-1 text-2xl text-[#3E2723] ${form.signature_style === "cursive" ? "italic font-serif" : "font-semibold"}`}>
          {form.signature_name || "Your Name"}{form.signature_credentials ? `, ${form.signature_credentials}` : ""}
        </div>
      </div>

      {historyOpen && (
        <div className="mt-5 rounded-xl border border-[#EFEBE0] bg-white p-4" data-testid="sig-history-panel">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-heading text-sm font-bold text-[#3E2723]">Signature change audit</div>
            <span className="rounded-full bg-[#FFB300] px-2 py-0.5 text-[9px] font-bold text-[#3E2723]">V1: audit trail only. Full rollback comes in V2.</span>
          </div>
          {history.items.length === 0 ? (
            <div className="py-4 text-center text-xs text-[#84675B]">No prior changes recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase text-[#7B6157]">
                  <tr><th className="py-1 pr-3">Changed at (UTC)</th><th className="py-1 pr-3">By</th><th className="py-1 pr-3">Fields changed</th><th className="py-1 pr-3">Image</th><th className="py-1">Hash</th></tr>
                </thead>
                <tbody>
                  {history.items.map((h) => (
                    <tr key={h.id} className="border-t border-[#F1EEE7]" data-testid={`sig-history-row-${h.id}`}>
                      <td className="py-1.5 pr-3 font-mono">{(h.changed_at || "").replace("T", " ").slice(0, 19)}</td>
                      <td className="py-1.5 pr-3">{h.changed_by_name || h.changed_by_email || "-"}</td>
                      <td className="py-1.5 pr-3">{(h.fields_changed || []).join(", ") || "-"}</td>
                      <td className="py-1.5 pr-3">{h.had_image ? "Yes" : "No"}</td>
                      <td className="py-1.5 font-mono text-[10px]">{(h.image_hash || "-").slice(0, 12)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 flex justify-end"><Btn onClick={save} disabled={busy} data-testid="sig-save-btn"><PenLine size={14} /> {busy ? "Saving…" : "Save Signature"}</Btn></div>
    </Card>
  );
}

function ComplianceSection() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/settings/compliance").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!d) return <div className="py-10 text-center text-sm text-[#84675B]">Running compliance checks…</div>;
  return (
    <Card className="p-6" data-testid="settings-compliance">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-heading text-base font-bold text-[#3E2723]">HIPAA Compliance Posture</h3>
          <p className="text-xs text-[#7B6157]">Live checks against your tenant's security configuration.</p>
        </div>
        <div className="text-right">
          <div data-testid="compliance-score" className={`font-heading text-3xl font-bold ${d.score >= 80 ? "text-[#2E7D32]" : d.score >= 60 ? "text-[#8D6E00]" : "text-[#C62828]"}`}>{d.score}%</div>
          <div className="text-[10px] uppercase font-semibold text-[#84675B]">Compliance score</div>
        </div>
      </div>
      <div className="space-y-2">
        {d.checks.map((c) => (
          <div key={c.key} data-testid={`compliance-check-${c.key}`} className="flex items-start gap-3 rounded-xl border border-[#F1EEE7] p-3">
            {c.status === "pass" ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#2E7D32]" /> : <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[#E6A700]" />}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#3E2723]">{c.name}</div>
              <div className="text-xs text-[#7B6157]">{c.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

const DOMAIN_OPTS = [["demographics", "Demographics"], ["clinical", "Clinical"], ["documents", "Documents"], ["billing", "Billing"]];

function CustomRolesSection() {
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [domains, setDomains] = useState([]);
  const load = () => api.get("/settings/roles").then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name.trim()) return toast.error("Role name is required");
    try {
      await api.post("/settings/roles", { name, description: desc, domains });
      toast.success("Custom role created"); setName(""); setDesc(""); setDomains([]); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (r) => {
    try { await api.delete(`/settings/roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="mt-5 p-6" data-testid="settings-custom-roles">
      <h3 className="mb-1 font-heading text-base font-bold text-[#3E2723]">Custom Roles</h3>
      <p className="mb-4 text-xs text-[#7B6157]">Define additional roles with specific edit-access domains (e.g., "Intake Coordinator" with demographics only).</p>
      {roles.length > 0 && (
        <div className="mb-4 space-y-2">
          {roles.map((r) => (
            <div key={r.id} data-testid={`custom-role-${r.key}`} className="flex items-center justify-between rounded-xl border border-[#F1EEE7] px-4 py-2.5">
              <div>
                <span className="text-sm font-semibold text-[#3E2723]">{r.name}</span>
                <span className="ml-2 text-xs text-[#7B6157]">{(r.domains || []).length ? r.domains.join(", ") : "read-only"}</span>
                {r.description && <div className="text-xs text-[#84675B]">{r.description}</div>}
              </div>
              <button data-testid={`custom-role-delete-${r.key}`} onClick={() => remove(r)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div><FLabel>Role name</FLabel><FInput data-testid="custom-role-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Intake Coordinator" /></div>
        <div><FLabel>Description</FLabel><FInput data-testid="custom-role-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Handles referrals & intake only" /></div>
      </div>
      <div className="mt-3">
        <FLabel>Edit access domains</FLabel>
        <div className="mt-1 flex flex-wrap gap-2">
          {DOMAIN_OPTS.map(([k, l]) => (
            <button key={k} data-testid={`custom-role-domain-${k}`} onClick={() => setDomains((d) => d.includes(k) ? d.filter((x) => x !== k) : [...d, k])}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${domains.includes(k) ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-4 flex justify-end"><Btn onClick={create} data-testid="custom-role-create-btn"><Plus size={14} /> Create Role</Btn></div>
    </Card>
  );
}

function CustomizationSection() {
  const [lists, setLists] = useState(null);
  const [drafts, setDrafts] = useState({});
  const LIST_META = [
    ["appointment_types", "Appointment Types", "Visit types offered in the Scheduler & client portal"],
    ["equipment_categories", "Equipment Categories", "Asset categories in Equipment Management"],
    ["equipment_statuses", "Equipment Statuses", "Lifecycle states for equipment"],
    ["refill_pharmacies", "Refill Pharmacies", "Pharmacy options for refill requests"],
  ];
  useEffect(() => { api.get("/custom-lists").then((r) => setLists(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!lists) return <div className="py-10 text-center text-sm text-[#84675B]">Loading…</div>;
  const addItem = async (key) => {
    const v = (drafts[key] || "").trim();
    if (!v) return;
    const items = [...(lists[key] || []), v];
    try {
      const r = await api.put(`/custom-lists/${key}`, { items });
      setLists((s) => ({ ...s, [key]: r.data.items }));
      setDrafts((s) => ({ ...s, [key]: "" }));
      toast.success("List updated - applies everywhere immediately");
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeItem = async (key, item) => {
    const items = (lists[key] || []).filter((x) => x !== item);
    if (items.length === 0) return toast.error("Keep at least one item");
    try {
      const r = await api.put(`/custom-lists/${key}`, { items });
      setLists((s) => ({ ...s, [key]: r.data.items }));
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="settings-customization">
      <Card className="p-5">
        <h3 className="font-heading text-base font-bold text-[#3E2723]">Customization Studio</h3>
        <p className="text-xs text-[#7B6157]">Make Cocoa EHR yours - every list below is fully customizable per clinic and applies across the whole app instantly.</p>
      </Card>
      <TaskStatusManager />
      {LIST_META.map(([key, label, desc]) => (
        <Card key={key} className="p-5" data-testid={`custom-list-${key}`}>
          <div className="mb-1 font-heading text-sm font-bold text-[#3E2723]">{label}</div>
          <p className="mb-3 text-xs text-[#84675B]">{desc}</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(lists[key] || []).map((item) => (
              <span key={item} className="group inline-flex items-center gap-1.5 rounded-full bg-[#F5F2EB] px-3 py-1.5 text-xs font-semibold text-[#5D4037]" data-testid={`custom-item-${key}-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {item.replace(/_/g, " ")}
                <button onClick={() => removeItem(key, item)} className="text-[#C62828] opacity-40 group-hover:opacity-100" title="Remove"><Trash2 size={11} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 max-w-sm">
            <FInput data-testid={`custom-add-input-${key}`} value={drafts[key] || ""} onChange={(e) => setDrafts((s) => ({ ...s, [key]: e.target.value }))}
              placeholder="Add new…" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem(key))} />
            <Btn data-testid={`custom-add-btn-${key}`} className="px-3 py-2 text-xs" onClick={() => addItem(key)}><Plus size={13} /> Add</Btn>
          </div>
        </Card>
      ))}
    </div>
  );
}

function PortalSettingsSection({ navigate }) {
  return (
    <div className="space-y-4" data-testid="settings-portal">
      <Card className="p-6">
        <h3 className="font-heading text-base font-bold text-[#3E2723]">Client Portal</h3>
        <p className="mt-1 text-sm text-[#6D4C41]">Clients get a separate, HIPAA-secure portal login where they can view appointments, request visits, see medications, ask for refills, sign consent documents, manage to-dos and update their own profile - and nothing more. Role isolation is enforced server-side: portal tokens can only reach <span className="font-mono text-xs">/api/portal/*</span> endpoints.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[["1. Invite", "Open any client's chart → Overview → Portal Access → Invite to Portal. A one-time temp password is generated."],
            ["2. First login", "Client picks 'Client Portal' on the sign-in page and must set a new password immediately."],
            ["3. Requests flow in", "Appointment & refill requests appear under Operations → Portal Requests for approval."],
            ["4. Everything is logged", "Every portal login, signature and profile change lands in the audit trail."]].map(([t, d]) => (
            <div key={t} className="rounded-xl border border-[#F1EEE7] p-4">
              <div className="text-sm font-bold text-[#3E2723]">{t}</div>
              <div className="mt-1 text-xs text-[#7B6157]">{d}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <Btn data-testid="portal-settings-clients-btn" onClick={() => navigate("/clients")}>Open Clients to invite</Btn>
          <Btn variant="outline" data-testid="portal-settings-requests-btn" onClick={() => navigate("/requests")}>View Portal Requests</Btn>
        </div>
      </Card>
    </div>
  );
}

function SecuritySection({ navigate }) {
  const items = [
    { title: "Two-Factor Authentication (TOTP)", desc: "Manage authenticator-app 2FA for your account.", action: "Open Security & 2FA", to: "/security" },
    { title: "Audit Trail", desc: "Every login, chart view, edit, print & archive is recorded per HIPAA. Filter by user, action and date range.", action: "Open Audit Trail", to: "/reports" },
    { title: "Session Timeout", desc: "Sessions automatically sign out after 15 minutes of inactivity, with a 60-second warning.", badge: "Enabled" },
    { title: "Field-Level Encryption", desc: "SSN and Medicaid IDs are encrypted at rest (AES-128 Fernet) and only decrypted for authorized API responses.", badge: "Active" },
    { title: "Rate Limiting & DDoS Mitigation", desc: "API traffic is throttled per IP (300 req/min general, 20 req/min on auth endpoints) with automatic 15-minute login lockout after 5 failed attempts.", badge: "Active" },
    { title: "Signature Integrity Registry", desc: "Every e-signature is stamped with a unique verification ID, timestamp, signer identity, IP and SHA-256 document hash for tamper detection.", badge: "Active" },
    { title: "Security Headers", desc: "HSTS, X-Frame-Options DENY, nosniff and strict referrer policy are applied to every response.", badge: "Active" },
    { title: "Portal Role Isolation", desc: "Client portal tokens are a separate JWT type that can only access /api/portal/* - clients can never reach staff or clinical endpoints.", badge: "Enforced" },
    { title: "Framework Alignment - HIPAA", desc: "Access control §164.312(a)(1), audit controls §164.312(b), integrity §164.312(c)(1) via signature hashing, person/entity authentication §164.312(d) via password + enforced TOTP 2FA, transmission security §164.312(e) via TLS/HSTS.", badge: "Aligned" },
    { title: "Framework Alignment - NIST SP 800-53", desc: "AC-2 account management (admin-gated portal activation/deactivation), AC-7 unsuccessful login lockout, AU-2 audit events, IA-2(1) MFA, SC-5 denial-of-service protection (rate limiting), SC-13 encryption of sensitive fields.", badge: "Aligned" },
    { title: "Framework Alignment - CMMC Level 2", desc: "AC.L2-3.1.1 authorized access control, AC.L2-3.1.8 limit unsuccessful logons, AU.L2-3.3.1 system auditing, IA.L2-3.5.3 multifactor authentication, SC.L2-3.13.16 protect data at rest.", badge: "Aligned" },
  ];
  return (
    <div className="space-y-5" data-testid="settings-security">
      <ComplianceSection />
      <div className="grid gap-4 md:grid-cols-2">
      {items.map((i) => (
        <Card key={i.title} className="p-5">
          <div className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><Lock size={14} className="text-[#7B6157]" /> {i.title}
            {i.badge && <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-bold text-[#2E7D32]">{i.badge}</span>}
          </div>
          <p className="mt-1.5 text-xs text-[#7B6157]">{i.desc}</p>
          {i.to && <Btn variant="outline" className="mt-3 px-3 py-1.5 text-xs" onClick={() => navigate(i.to)}><ExternalLink size={12} /> {i.action}</Btn>}
        </Card>
      ))}
      </div>
    </div>
  );
}

function PermissionsConsole() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openMod, setOpenMod] = useState(null);
  useEffect(() => { api.get("/settings/permissions").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!d) return <div className="py-10 text-center text-sm text-[#84675B]">Loading permission matrix…</div>;
  const toggle = (mod, action, role) => {
    if (role === "admin" && action === "view") return;
    setD((s) => {
      const roles = s.matrix[mod][action];
      return { ...s, matrix: { ...s.matrix, [mod]: { ...s.matrix[mod], [action]: roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role] } } };
    });
  };
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/settings/permissions", { matrix: d.matrix }); setD(r.data); toast.success("Permission matrix saved - enforced server-side immediately"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-6" data-testid="settings-permissions-console">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="font-heading text-base font-bold text-[#3E2723]">Enterprise Permissions Console</h3>
        <Btn onClick={save} disabled={busy} data-testid="perm-save-btn">{busy ? "Saving…" : "Save Matrix"}</Btn>
      </div>
      <p className="mb-5 text-xs text-[#7B6157]">Granular access control: every EHR module × {d.actions.length} actions (view / create / edit / delete / sign / export) per role - including custom roles. Administrators always retain view access. Click a module to expand its action grid.</p>
      <div className="space-y-2">
        {d.modules.map((m) => (
          <div key={m.key} className="rounded-xl border border-[#EFEBE0]" data-testid={`perm-module-${m.key}`}>
            <button data-testid={`perm-module-toggle-${m.key}`} onClick={() => setOpenMod(openMod === m.key ? null : m.key)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-[#FDFCF8]">
              <span className="text-sm font-semibold text-[#3E2723]">{m.label}</span>
              <span className="flex items-center gap-2 text-xs text-[#7B6157]">
                {d.actions.filter((a) => (d.matrix[m.key]?.[a] || []).length > 0).length} actions configured
                {openMod === m.key ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </span>
            </button>
            {openMod === m.key && (
              <div className="overflow-x-auto border-t border-[#F1EEE7] px-4 pb-4">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-[#7B6157]">
                      <th className="py-2 pr-3">Action</th>
                      {d.roles.map((r) => <th key={r} className="px-2 py-2 text-center whitespace-nowrap">{d.role_labels[r] || r}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {d.actions.map((a) => (
                      <tr key={a} className="border-t border-[#F5F2EB]">
                        <td className="py-2 pr-3 text-xs font-semibold capitalize text-[#5D4037]">{a}</td>
                        {d.roles.map((role) => {
                          const on = (d.matrix[m.key]?.[a] || []).includes(role);
                          const locked = role === "admin" && a === "view";
                          return (
                            <td key={role} className="px-2 py-1.5 text-center">
                              <button data-testid={`perm-${m.key}-${a}-${role}`} disabled={locked} aria-pressed={on} onClick={() => toggle(m.key, a, role)}
                                className={`h-5 w-9 rounded-full transition-colors relative ${on ? "bg-[#4E342E]" : "bg-[#D7CCC8]"} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}>
                                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? "translate-x-[18px]" : "translate-x-0.5"} left-0`} />
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Settings() {
  const { section = "organization" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  // HubSpot-style grouping. Every group has a clear intent; V2 items live
  // beside their peers with a small "V2" pill so the shape of the roadmap
  // is visible without cluttering V1 workflows.
  const groups = isAdmin ? [
    { title: "General", items: [
      { key: "organization", label: "Organization Profile", icon: Building2 },
      { key: "branding", label: "Branding & Forms", icon: Palette },
      { key: "facilities", label: "Facilities & Rooms", icon: DoorOpen },
      { key: "customization", label: "Custom Lists & Statuses", icon: Layers },
      { key: "signature", label: "My Signature", icon: PenLine },
    ]},
    { title: "Users & Teams", items: [
      { key: "users", label: "Users & Roles", icon: Users },
      { key: "rbac", label: "Roles & Permissions", icon: ShieldCheck },
    ]},
    { title: "Clinical", items: [
      { key: "programs", label: "Programs", icon: Boxes },
      { key: "diagnosis-library", label: "Diagnosis Library", icon: BookOpen, fresh: true },
      { key: "medication-library", label: "Medication Library", icon: Pill, fresh: true },
      { key: "pharmacy-directory", label: "Pharmacy Directory", icon: Store, fresh: true },
      { key: "prescription-log", label: "Communication Log", icon: PhoneCall, fresh: true },
      { key: "patient-education", label: "Patient Education Library", icon: GraduationCap, fresh: true },
      { key: "signatures", label: "Signature Audit", icon: FileSignature, v2: true },
      { key: "configuration", label: "Configuration Library", icon: Wand2, v2: true },
      { key: "ddi-audit", label: "DDI Override Audit", icon: ShieldAlert, v2: true },
    ]},
    { title: "Privacy & Security", items: [
      { key: "security", label: "Security & Compliance", icon: Lock },
      { key: "session-timeout", label: "Session Timeout", icon: Cog },
      { key: "baa", label: "BAA Tracker", icon: FileSignature, v2: true },
      { key: "audit-report", label: "Complete Audit Report", icon: ShieldCheck },
      { key: "data-retention", label: "Data Retention", icon: Archive },
    ]},
    { title: "Integrations", items: [
      { key: "portal", label: "Client Portal", icon: ExternalLink },
      { key: "portal-feedback", label: "Feedback & Surveys", icon: MessageSquare },
      { key: "billing", label: "Billing & Subscription", icon: CreditCard },
      ...(user?.is_super_admin ? [{ key: "plans", label: "Plan Management", icon: Layers }] : []),
    ]},
  ] : [
    { title: "Account", items: [{ key: "signature", label: "My Signature", icon: PenLine }] },
  ];
  const sections = groups.flatMap((g) => g.items);
  const active = sections.some((s) => s.key === section) ? section : sections[0].key;
  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Enterprise administration console: general, users, clinical, privacy, and integrations" />
      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="lg:w-64 shrink-0 flex lg:flex-col gap-1 overflow-x-auto" aria-label="Settings sections">
          {groups.map((g) => (
            <div key={g.title} className="lg:mb-2 flex lg:block gap-1">
              <div className="hidden lg:block px-3.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[#84675B]">{g.title}</div>
              {g.items.map(({ key, label, icon: Icon, v2, fresh }) => (
                <button key={key} data-testid={`settings-nav-${key}`} onClick={() => navigate(`/settings/${key}`)}
                  className={`flex items-center gap-2.5 whitespace-nowrap rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors w-full ${active === key ? "bg-[#4E342E] text-white" : "text-[#5D4037] hover:bg-[#F5F2EB]"}`}>
                  <Icon size={16} className="shrink-0" /> <span className="min-w-0 flex-1 text-left lg:whitespace-normal lg:leading-tight">{label}</span>{fresh && <NewPill feature={key} />}
                  {v2 && <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${active === key ? "bg-[#FFB300]/25 text-[#FFB300]" : "bg-[#FFB300] text-[#3E2723]"}`}>
                    <Sparkles size={9} /> V2
                  </span>}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="flex-1 min-w-0">
          {active === "organization" && <OrganizationSection />}
          {active === "branding" && <BrandingSection />}
          {active === "customization" && <CustomizationSection />}
          {active === "portal" && <PortalSettingsSection navigate={navigate} />}
          {active === "signature" && <SignatureSection />}
          {active === "users" && <UsersPage />}
          {active === "rbac" && <RolesPermissions MatrixConsole={<PermissionsConsole />} />}
          {active === "programs" && <ProgramsConfig />}
          {active === "diagnosis-library" && <DiagnosisLibrarySection />}
          {active === "medication-library" && <MedicationLibrarySection />}
          {active === "pharmacy-directory" && <PharmacyDirectorySection />}
          {active === "prescription-log" && <CommunicationLogSection />}
          {active === "patient-education" && <EducationLibrarySection />}
          {active === "facilities" && <FacilitiesRooms />}
          {active === "portal-feedback" && <FeedbackSurveys />}
          {active === "billing" && <Billing />}
          {active === "plans" && user?.is_super_admin && <PlansSection />}
          {active === "security" && <SecuritySection navigate={navigate} />}
          {active === "audit-report" && <AuditReportSection />}
          {active === "data-retention" && <DataRetentionSection />}
          {active === "signatures" && <SignatureAuditSection />}
          {active === "configuration" && <ConfigurationLibrarySection />}
          {active === "ddi-audit" && <DdiAuditSection />}
          {active === "session-timeout" && <SessionTimeoutSection />}
          {active === "baa" && <BaaTrackerV2Preview />}
        </div>
      </div>
    </div>
  );
}
